/**
 * Formats a snake_case metric name into a human-readable label
 * @param metric - The metric name in snake_case format
 * @returns A human-readable formatted string
 *
 * @example
 * formatMetricName('cpu_usage') // 'CPU Usage'
 * formatMetricName('enclosure_temperature') // 'Enclosure Temperature'
 */
export function formatMetricName(metric: string): string {
  if (!metric) return '';

  // Common acronyms that should be fully uppercased
  const acronyms = new Set([
    'cpu',
    'gpu',
    'ram',
    'io',
    'id',
    'url',
    'api',
    'http',
    'https',
    'ssl',
    'tls',
  ]);

  return metric
    .split('_')
    .map((word) => {
      // Check if word is a known acronym
      if (acronyms.has(word.toLowerCase())) {
        return word.toUpperCase();
      }
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Formats a category name into a human-readable label
 * @param category - The category name (e.g., 'machine-health')
 * @returns A human-readable formatted string
 *
 * @example
 * formatCategoryName('machine-health') // 'Machine Health'
 */
export function formatCategoryName(category: string): string {
  if (!category) return '';

  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats a unit string for display
 * @param unit - The unit string (e.g., 'degC', 'kPa', 'mm/s')
 * @returns A formatted unit string
 *
 * @example
 * formatUnit('degC') // '°C'
 * formatUnit('kPa') // 'kPa'
 */
export function formatUnit(unit: string | undefined): string {
  if (!unit) return '';

  // Handle special case for degrees Celsius
  if (unit === 'degC') {
    return '°C';
  }

  return unit;
}

export function isBooleanUnit(unit: string | null | undefined): boolean {
  return (unit ?? '').trim().toLowerCase() === 'bool';
}

export function isMachineAlarmSignal({
  unit,
  signalCategory,
}: {
  unit?: string | null;
  signalCategory?: string | null;
}): boolean {
  const normalizedCategory = (signalCategory ?? '').trim().toLowerCase();

  return (
    isBooleanUnit(unit) ||
    normalizedCategory === 'machine-alarms' ||
    normalizedCategory === 'machinealarm'
  );
}

export function formatBooleanStateValue(
  value: number | null | undefined
): string {
  if (value == null || Number.isNaN(value)) return '-';
  return value >= 0.5 ? 'on' : 'off';
}

export function formatAlarmUnit(unit: string | null | undefined): string {
  if (isBooleanUnit(unit)) return 'on/off';
  return formatUnit(unit ?? undefined);
}

/**
 * Formats a duration string (HH:MM:SS) into a human-readable format
 * @param duration - Duration in HH:MM:SS format (e.g., '00:05:00')
 * @returns A human-readable duration string
 *
 * @example
 * formatDuration('00:05:00') // '5 min'
 * formatDuration('01:30:00') // '1h 30min'
 * formatDuration('00:00:30') // '30 sec'
 */
export function formatDuration(duration: string | undefined): string {
  if (!duration) return '';

  const parts = duration.split(':');
  if (parts.length !== 3) return duration;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  const result: string[] = [];

  if (hours > 0) {
    result.push(`${hours}h`);
  }
  if (minutes > 0) {
    result.push(`${minutes} min`);
  }
  if (seconds > 0 && hours === 0) {
    result.push(`${seconds} sec`);
  }

  return result.join(' ') || '0 sec';
}

interface TriggerInfo {
  triggerMode: string;
  triggerValue: number;
  evalWindowSize?: string | null;
}

/**
 * The trigger logic of an alarm, worded the way the alarms table says it.
 * The notification detail reads the same value from here.
 *
 * @example
 * formatTrigger({ triggerMode: 'Percentage', triggerValue: 5, evalWindowSize: '00:05:00' }) // '5% over 5 min'
 */
export function formatTrigger({
  triggerMode,
  triggerValue,
  evalWindowSize,
}: TriggerInfo): string {
  const window = formatDuration(evalWindowSize ?? undefined);

  if (triggerMode === 'Percentage') {
    return `${triggerValue}% over ${window}`;
  }
  if (triggerMode === 'Period') {
    return `${triggerValue}s over ${window}`;
  }
  if (triggerMode === 'Spikes') {
    return `${triggerValue} spikes in ${window}`;
  }
  return `${triggerValue}`;
}

/**
 * Gets the unit for a signal based on known signal-to-unit mappings
 * This is a fallback when the unit is not stored with the alarm
 * @param signal - The signal name (e.g., 'enclosure_temperature', 'gpu_usage')
 * @returns The unit string or empty string if unknown
 */
export function getSignalUnit(signal: string | undefined): string {
  if (!signal) return '';

  const signalLower = signal.toLowerCase();

  // Temperature signals
  if (
    signalLower.includes('temperature') ||
    signalLower.includes('temp') ||
    signalLower === 'machine_temp' ||
    signalLower === 'ambient_temp' ||
    signalLower === 'sensor_temp'
  ) {
    return '°C';
  }

  // Percentage signals
  if (
    signalLower.includes('usage') ||
    signalLower.includes('efficiency') ||
    signalLower.includes('uptime') ||
    signalLower.includes('accuracy') ||
    signalLower.includes('reject_rate') ||
    signalLower === 'gpu_usage' ||
    signalLower === 'cpu_usage'
  ) {
    return '%';
  }

  // Pressure signals
  if (
    signalLower.includes('pressure') ||
    signalLower === 'air_pressure' ||
    signalLower === 'hydraulic_pressure'
  ) {
    return 'bar';
  }

  // Throughput signals
  if (signalLower.includes('throughput') || signalLower.includes('flow')) {
    return 'items/min';
  }

  return '';
}

export interface ThresholdInfo {
  thresholdMode: string;
  thresholdLow: number;
  thresholdHigh: number;
  unit?: string | null;
  signal?: string | null;
  signalCategory?: string | null;
}

/**
 * Formats an alarm's threshold configuration into a human-readable string,
 * accounting for boolean machine-alarm signals (on/off) vs numeric signals.
 *
 * @example
 * formatThreshold({ thresholdMode: 'AboveHigh', thresholdLow: 0, thresholdHigh: 85, unit: 'degC' }) // '> 85 °C'
 */
export function formatThreshold({
  thresholdMode,
  thresholdLow,
  thresholdHigh,
  unit,
  signal,
  signalCategory,
}: ThresholdInfo): string {
  const resolvedUnit = unit || getSignalUnit(signal ?? undefined);
  const isMachineAlarm = isMachineAlarmSignal({
    unit: resolvedUnit,
    signalCategory,
  });
  const unitSuffix = resolvedUnit ? ` ${formatUnit(resolvedUnit)}` : '';

  if (isMachineAlarm) {
    if (thresholdMode === 'OutsideRange') {
      return (
        `${formatBooleanStateValue(thresholdLow)} / ` +
        `${formatBooleanStateValue(thresholdHigh)}`
      );
    }
    if (thresholdMode === 'BelowLow') {
      return formatBooleanStateValue(thresholdLow);
    }
    if (thresholdMode === 'AboveHigh') {
      return formatBooleanStateValue(thresholdHigh);
    }
    return '';
  }

  if (thresholdMode === 'OutsideRange') {
    return `${thresholdLow}${unitSuffix} - ${thresholdHigh}${unitSuffix}`;
  }
  if (thresholdMode === 'BelowLow') {
    return `< ${thresholdLow}${unitSuffix}`;
  }
  if (thresholdMode === 'AboveHigh') {
    return `> ${thresholdHigh}${unitSuffix}`;
  }

  return '';
}
